import React, { useCallback } from 'react';
import { Pressable } from 'react-native';
import { useRouter, usePathname } from 'expo-router';
import {
  accent,
  green,
  white,
  tabsBarShadow,
  sand,
} from '@/shared/styles/colors';
import { Icon } from 'react-native-paper';
import styled from '@emotion/native';
import { ROUTES } from '@/shared/constants/routes';
import {
  STYLIST_TAB_ITEMS,
  CLIENT_TAB_ITEMS,
} from '@/shared/constants/navigation';
import type { RouteType } from '@/shared/types/navigation';
import { BOTTOM_TAB_BAR_HEIGHT } from '@/shared/styles/constants';
import useStore from '@/stores/useStore';
import { useShallow } from 'zustand/react/shallow';
import { useIsMessagesChannel } from '@/shared/hooks/useIsMessagesChannel';

export default function TabBar() {
  const router = useRouter();
  const pathname = usePathname();

  // Optimize store selectors with useShallow
  const { isStylist } = useStore(
    useShallow((state) => ({
      isStylist: state.isStylist,
    })),
  );

  const isInChat = useIsMessagesChannel();

  const isRouteActive = useCallback(
    (route: RouteType) => {
      return pathname === route.path;
    },
    [pathname],
  );

  const handlePress = useCallback(
    (route: RouteType) => {
      if (pathname === route.path) return;
      router.push(route.path);
    },
    [pathname, router],
  );

  const tabItems = isStylist ? STYLIST_TAB_ITEMS : CLIENT_TAB_ITEMS;

  const isInstantStylingScreen = pathname === ROUTES.INSTANT_STYLING.path;
  const showAlternateTabBar = isInstantStylingScreen && !isStylist;
  const isVideoSession = pathname === ROUTES.VIDEO_SESSION.path;
  const shouldHideTabBar = isInChat || isVideoSession;

  const getIconColor = useCallback(
    (route: RouteType) => {
      if (showAlternateTabBar) {
        return green;
      } else if (isRouteActive(route)) {
        return accent;
      } else {
        return white;
      }
    },
    [showAlternateTabBar, isRouteActive],
  );

  return (
    <OuterContainer style={{ display: shouldHideTabBar ? 'none' : 'flex' }}>
      <Container altTabStyle={showAlternateTabBar}>
        <TabContainer>
          {tabItems.map((item) => {
            const iconColor = getIconColor(item.route);

            return (
              <TabButton
                key={item.route.label}
                onPress={() => handlePress(item.route)}
              >
                <Icon
                  source={item.icon.source}
                  size={item.icon.size}
                  color={iconColor}
                />
              </TabButton>
            );
          })}
        </TabContainer>
      </Container>
    </OuterContainer>
  );
}

const OuterContainer = styled.View({});

const Container = styled.View<{ altTabStyle?: boolean }>(({ altTabStyle }) => ({
  backgroundColor: altTabStyle ? sand : green,
  height: BOTTOM_TAB_BAR_HEIGHT,
}));

const TabContainer = styled.View({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'flex-end',
  height: '100%',
});

const TabButton = styled(Pressable)<{ backgroundColor?: string }>(
  ({ backgroundColor }) => ({
    height: 36,
    width: 36,
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 28,
    backgroundColor: backgroundColor,
    borderRadius: 50,
  }),
);
